import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  Input,
  type Signal,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute } from '@angular/router';
import {
  ButtonsContainerComponent,
  queryParam$,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import { snapshot } from '@principle-theorem/shared';
import { AuthService } from '../auth.service';

interface IEmailLinkForm {
  email: string;
}

@Component({
  selector: 'pt-passwordless-login',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatIconModule,
    MatInputModule,
    MatButton,
    ButtonsContainerComponent,
  ],
  templateUrl: './passwordless-login.component.html',
  styleUrl: './passwordless-login.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PasswordlessLoginComponent {
  form = new TypedFormGroup<IEmailLinkForm>({
    email: new TypedFormControl(undefined, Validators.required),
  });

  @Input({ required: true }) appRedirectUrl: string;
  readonly expectedEmail = input<string>();

  linkSent = signal(false);
  submittedEmail = signal('');
  linkSendError = signal(false);

  readonly enteredEmail = toSignal(this.form.controls.email.valueChanges);
  readonly isUnexpectedEmail: Signal<boolean> = computed(() => {
    const expectedEmail = this.expectedEmail();
    const enteredEmail = this.enteredEmail();
    if (!expectedEmail || !enteredEmail) {
      return false;
    }
    return (
      PasswordlessLoginComponent._normalise(expectedEmail) !==
      PasswordlessLoginComponent._normalise(enteredEmail)
    );
  });

  constructor(
    private _auth: AuthService,
    private _route: ActivatedRoute
  ) {
    effect(() => {
      const expectedEmail = this.expectedEmail();
      if (expectedEmail && !this.form.controls.email.value) {
        this.form.controls.email.setValue(expectedEmail);
      }
    });
  }

  private static _normalise(email: string): string {
    return email.trim().toLowerCase();
  }

  async sendLoginLink(): Promise<void> {
    const { email } = this.form.getRawValue();
    const redirectTo = await snapshot(queryParam$(this._route, 'redirectTo'));

    this.linkSent.set(false);
    this.linkSendError.set(false);

    try {
      await this._auth.sendPassowrdlessLoginLink(
        email,
        this._continueUrl(redirectTo)
      );
      this.submittedEmail.set(email);
      this.linkSent.set(true);
    } catch {
      this.linkSendError.set(true);
    }
  }

  /**
   * Resolves `redirectTo` (a path that may carry a query string, such as a
   * referral's login hint) against the app URL. Anything pointing at another
   * origin falls back to the app URL so the sign-in link cannot redirect away.
   */
  private _continueUrl(redirectTo?: string): string {
    if (!redirectTo) {
      return this.appRedirectUrl;
    }
    const appUrl = new URL(this.appRedirectUrl);
    const continueUrl = new URL(redirectTo, appUrl);
    if (continueUrl.origin !== appUrl.origin) {
      return this.appRedirectUrl;
    }
    return continueUrl.toString();
  }
}
